<template>
    <div>
        <page-header>Invites</page-header>

        <vx-alert v-if="acceptedInvitation" class="mb-4" color="success" :closeable="false" shadow>
            You have successfully joined the <strong>{{ acceptedInvitation.team.name }}</strong> team!
        </vx-alert>

        <page-loading v-if="loading && !invitations.length"></page-loading>

        <vx-box
            color="info"
            v-else-if="!loading && !invitations.length"
            class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
        >
            There are no pending invitations.
        </vx-box>

        <vx-table v-else-if="invitations.length" class="xl:grid-cols-3-auto" rowBreakpoint="xl">
            <vx-table-row isHeader>
                <div class="xl:col-span-2">Team</div>
            </vx-table-row>

            <vx-table-row v-for="(invitation, index) in invitations" :key="index" data-test="invite-row">
                <vx-table-row-title class="xl:col-span-2!" data-test="team-name">
                    {{ invitation.team.name }}
                    <vx-badge
                        v-if="invitation.team.require_sso"
                        size="xs"
                        color="sky"
                        class="ml-2"
                        data-test="requires-sso-badge"
                    >
                        Requires SSO
                    </vx-badge>
                </vx-table-row-title>

                <vx-table-row-buttons>
                    <vx-dropdown placement="right">
                        <vx-button
                            data-test="dropdown"
                            aria-label="Invitation actions"
                            type="button"
                            color="muted"
                            outline
                            class="w-[45px]"
                        >
                            <font-awesome-icon widthAuto :icon="faEllipsis" />
                        </vx-button>

                        <template v-slot:items>
                            <vx-dropdown-item data-test="reject-button" @click="reject(invitation)">
                                Reject
                                <font-awesome-icon widthAuto :icon="faXmark"></font-awesome-icon>
                            </vx-dropdown-item>
                        </template>
                    </vx-dropdown>

                    <vx-button
                        @click.prevent="accept(invitation)"
                        data-test="accept-button"
                        color="muted"
                        class="w-full sm:w-auto"
                        outline
                        :loading="accepting === invitation.id"
                    >
                        <template v-if="acceptsWithSso(invitation)">
                            Log In with SSO <font-awesome-icon widthAuto :icon="faRightToBracket" />
                        </template>
                        <template v-else>Accept <font-awesome-icon widthAuto :icon="faCheck" /></template>
                    </vx-button>
                </vx-table-row-buttons>
            </vx-table-row>
        </vx-table>
    </div>
</template>

<script setup>
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import axios from '~/services/axios';
import {
    VxAlert,
    VxBadge,
    VxBox,
    VxButton,
    VxTable,
    VxTableRow,
    VxTableRowTitle,
    VxTableRowButtons,
    VxDropdown,
    VxDropdownItem,
    VxSwal,
} from '@voxie/frontend-components';
import { ref } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCheck, faEllipsis, faRightToBracket, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useToasted } from '~/composables/useToasted';
import { throwSwalRequestError } from '~/utils/request';
import { stringifyQueryString } from '~/utils/string';

const toasted = useToasted();
const loading = ref(false);
const accepting = ref();
const acceptedInvitation = ref();
const invitations = ref([]);

const getPendingInvitations = () => {
    if (loading.value) {
        return;
    }

    loading.value = true;

    axios
        .get('/settings/invitations/pending')
        .then((response) => {
            invitations.value = response.data;
        })
        .catch((e) => {
            console.error(e);
            toasted.global.platform_error();
        })
        .finally(() => {
            loading.value = false;
        });
};

const reject = (invitation) => {
    acceptedInvitation.value = undefined;
    VxSwal.confirmation('Reject Invite', 'Are you sure? There is no undo.', 'danger', () =>
        axios
            .post(`/settings/invitations/${invitation.id}/reject`)
            .then(() => getPendingInvitations())
            .catch(throwSwalRequestError)
    );
};

const acceptsWithSso = (invitation) => Boolean(invitation.locked && invitation.sso_login_url);

const accept = (invitation) => {
    acceptedInvitation.value = undefined;

    if (acceptsWithSso(invitation)) {
        // link=1: the IdP identity must resolve to this signed-in account; the ACS accepts the invitation
        window.location.assign(
            `${invitation.sso_login_url}?${stringifyQueryString({
                invitation: invitation.token,
                link: 1,
                intended: '/settings#/invites',
            })}`
        );
        return;
    }

    accepting.value = invitation.id;
    axios
        .post(`/settings/invitations/${invitation.id}/accept`)
        .then(() => {
            Bus.$emit('updateTeams');
            acceptedInvitation.value = invitation;

            getPendingInvitations();
        })
        .catch((e) => {
            console.error(e);
            toasted.global.platform_error();
        })
        .finally(() => {
            accepting.value = undefined;
        });
};

getPendingInvitations();
</script>
